import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { getSiteName, getSiteString } from "@src/utils/key-parser";
import { sortCameras, type CameraSortDirection, type CameraSortOption } from "./camera-sort";

export type CameraSiteGroup = {
  siteId: string;
  siteName: string;
  siteSubtitle?: string;
  cameras: CameraApiResponse[];
};

export function groupCamerasBySite(
  cameras: CameraApiResponse[],
  sortBy: CameraSortOption,
  siteNames?: ReadonlyMap<string, string>,
  direction: CameraSortDirection = "asc",
): CameraSiteGroup[] {
  if (!cameras || cameras.length === 0) return [];

  const sorted = sortCameras(cameras, sortBy, siteNames, direction);
  const groups = new Map<string, CameraSiteGroup>();
  const orderedGroups: CameraSiteGroup[] = [];

  for (const camera of sorted) {
    const siteId = getSiteString(camera);
    let group = groups.get(siteId);

    if (!group) {
      const { name, subtitle } = getSiteName(camera, siteId, siteNames);
      group = {
        siteId,
        siteName: name,
        siteSubtitle: subtitle,
        cameras: [],
      };
      groups.set(siteId, group);
      orderedGroups.push(group);
    }

    group.cameras.push(camera);
  }

  // Sorting by site already leaves the groups in site order (either
  // direction). Any other sort only orders cameras within each site, so put
  // the sites in name order rather than wherever their first camera landed.
  if (sortBy !== "site") {
    orderedGroups.sort((a, b) => a.siteName.localeCompare(b.siteName) || a.siteId.localeCompare(b.siteId));
  }

  return orderedGroups;
}
